import { Injectable } from '@angular/core';
import { HttpService } from 'projects/customer/src/app/shared/services/http-service.service';
import { HttpClient } from '@angular/common/http';
import { Service, Resource } from 'ngx-jsonapi';
import { BehaviorSubject, EMPTY, Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { NotificationService } from '../../../shared/services/notification.service';
import { DataTransferService } from '../../../shared/services/data-transfer.service';
import { AbstractControl, FormGroup } from '@angular/forms';
import { ModuleType } from '../../../shared/utils/common-interface';
import { JdDocuments, SellerPdCode } from '../../../shared/utils/jd-seller';

// ---- Camera Evidence (WMS camera media, read-only) ----
export type OrderVariant = 'b2c' | 'b2b';

export interface EvidenceItem {
    id: string;
    hash_key: string;
    media_type: 'image' | 'video';
    stage: string;
    file_name: string;
    status: string;
    duration: number | null;
    device: string;
    created_at: string;
    created_by: string;
    sku: string | null;
    level: string | null;
    order_number: string | null;
    shipment_number: string | null;
    grn_number: string | null;
    consignment_number: string | null;
}

export interface EvidenceFilters {
    mediaType?: 'image' | 'video';
    stage?: string;
    status?: string;
    from?: string; // YYYY-MM-DD
    to?: string; // YYYY-MM-DD
}

export interface EvidenceCollectionMeta {
    current_page: number | null;
    total_count: number;
    total_pages: number | null;
    next_page: number | null;
    prev_page: number | null;
}

export interface EvidenceListResponse {
    result?: number;
    data: EvidenceItem[];
    meta?: EvidenceCollectionMeta;
}

export interface MediaUrlResponse {
    result?: number;
    data?: { url?: string };
}

// Keep in sync with the pagination component's default selection (10) so the
// first paint matches what the page-size selector shows.
export const CAMERA_EVIDENCE_PAGE_SIZE = 10;

// Stage keys as returned by the camera_evidence API.
const CAMERA_EVIDENCE_STAGE_LABELS: { [key: string]: string } = {
    count_qc: 'Count & QC',
    packing: 'Packing',
    packing_material: 'Packing Material',
    putaway: 'Putaway',
    quality_check: 'Quality Check',
    receiving: 'Receive'
};

export class Order extends Resource {
    public attributes = {
        type: '',
        id: ''
    };
}
@Injectable({
    providedIn: 'root'
})
export class OrderService extends Service<Order> {
    timeZone: any;
    private logoImageCache = new Map<string, Observable<any>>();

    constructor(
        private http: HttpClient,
        public httpService: HttpService,
        public notificationService: NotificationService,
        private dataTransferService: DataTransferService
    ) {
        super();
        this.getTimezone();
    }

    getTimezone() {
        const date = new Date();
        const new_str = date.toString();
        const checkPlusMinus = new_str.slice(28, 29);
        const result = new_str.slice(29, 33);
        if (checkPlusMinus == '+') {
            const a = '%2B';
            const b = result;
            const c = '%3A';
            this.timeZone = [a, b.slice(0, 2), c, b.slice(2)].join('');
        }
        if (checkPlusMinus == '-') {
            const a = '-';
            const b = result;
            const c = '%3A';
            this.timeZone = [a, b.slice(0, 2), c, b.slice(2)].join('');
        }
    }
    getCountryList() {
        const path = 'v1/masters/countries';
        return this.httpService.callAuthGetMasterApi(path);
    }
    bulkApproveOrders(params) {
        let path = 'orders/bulk_approve_or_reject_oms_order';
        return this.httpService.callAuthPostApi(path, params, 'jsonApi');
    }
    approveRejectShipment(omsorderId, params) {
        const data = {
            to_approve: params
        };
        let path = `orders/${omsorderId}/approve_or_reject_oms_order`;
        return this.httpService.callAuthPostApi(path, data, 'jsonApi');
    }

    cancelOrder(order_id, param) {
        const path = `orders/cancel_b2c/${order_id}`;
        return this.httpService.callPutApi(path, param, 'jsonApi');
    }

    holdOrder(
        orderId: number,
        params: { comment?: string } = { comment: 'Hold Pot' }
    ) {
        const path = `orders/${orderId}/hold`;
        return this.httpService.callAuthPostApi(path, params, 'jsonApi');
    }

    bulkHoldOrders(params: { order_ids: number[]; comment?: string }) {
        const path = `orders/hold/bulk`;
        return this.httpService.callAuthPostApi(path, params, 'jsonApi');
    }

    /**
     * PD code dropdown (OEM-7340): this seller's assigned Outbound codes, sorted by code.
     * Answers 403 without PD_CODE_READ.
     */
    getSellerPdCodes(): Observable<SellerPdCode[]> {
        return this.httpService
            .callAuthGetApi('orders/pd_codes', 'jsonApi')
            .pipe(map((response: any) => (Array.isArray(response?.data) ? response.data : [])));
    }

    /** One PD code list for every PD Code cell of a grid page (see resetSellerPdCodes). */
    private sellerPdCodes$: Observable<SellerPdCode[]> | null = null;

    getSharedSellerPdCodes(): Observable<SellerPdCode[]> {
        if (!this.sellerPdCodes$) {
            this.sellerPdCodes$ = this.getSellerPdCodes().pipe(
                catchError((error) => {
                    this.sellerPdCodes$ = null;
                    return throwError(() => error);
                }),
                shareReplay({ bufferSize: 1, refCount: false })
            );
        }
        return this.sellerPdCodes$;
    }

    /** Called when a grid page opens, so newly assigned PD codes show up. */
    resetSellerPdCodes(): void {
        this.sellerPdCodes$ = null;
    }

    /**
     * Assign PD Code bulk action of Action Required (OEM-7340): one code for 1 to 500
     * orders. Needs Action Required write and PD_CODE_WRITE.
     */
    bulkAssignPdCode(params: { order_ids: number[]; pd_code_id: number }): Observable<any> {
        return this.httpService.callAuthPostApi('orders/bulk_assign_pd_code', params, 'jsonApi');
    }

    releaseHold(
        orderId: number,
        params: { comment?: string } = { comment: 'Hold Pot' }
    ) {
        const path = `orders/${orderId}/release_hold`;
        return this.httpService.callAuthPostApi(path, params, 'jsonApi');
    }

    bulkReleaseHold(params: { order_ids: number[]; comment?: string }) {
        const path = `orders/hold/bulk_release`;
        return this.httpService.callAuthPostApi(path, params, 'jsonApi');
    }

    getServiceMapping(orderIds: number[]) {
        const path = `orders/service_mapping?order_ids=${orderIds.join(',')}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    bulkAssignAndUnhold(payload: {
        order_ids: number[];
        bulk_action: boolean;
        service_mapping: Array<{
            marketplace: string;
            shipping_method: string;
            seller_shipping_type?: string;
            product_type: number;
            carrier_id: number;
        }>;
    }) {
        const path = `orders/bulk_assign_and_unhold`;
        return this.httpService.callAuthPostApi(path, payload, 'jsonApi');
    }

    getOrderSubStatus() {
        const path = 'v1/masters/shipment_sub_status';
        return this.httpService.callAuthGetMasterApi(path);
    }

    getTrackingShipment?(orderId) {
        const path = 'orders/' + orderId + '/track_shipment';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getStateList(id) {
        const path = 'v1/masters/states/' + id;
        return this.httpService.callAuthGetMasterApi(path);
    }

    getStockConditions() {
        const path = 'v1/masters/stock_conditions?type=b2c';
        return this.httpService.callAuthGetMasterApi(path);
    }

    getCustomDuty() {
        const path = 'v1/masters/custom_duty';
        return this.httpService.callAuthGetMasterApi(path);
    }

    getCustomType() {
        const path = 'v1/masters/custom_type';
        return this.httpService.callAuthGetMasterApi(path);
    }

    getPaymentModes() {
        const path = 'v1/masters/payment_modes';
        return this.httpService.callAuthGetMasterApi(path);
    }

    getCurrencyData() {
        const path = 'v1/masters/currency';
        return this.httpService.callAuthGetMasterApi(path);
    }

    getCarriers() {
        const path = 'v1/masters/1/carriers';
        return this.httpService.callAuthGetMasterApi(path);
    }

    getShippingTypes(called_for?, product_type?) {
        let path = `orders/shipping_types`;
        if (called_for) {
            path = path + `?called_for=${called_for}`;
        }
        if (product_type) {
            path = path + `&filter[product_type]=${product_type}`;
        }
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getReasonForExport(shipping_type) {
        const path = `orders/reason_for_exports?service_type=${shipping_type}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    searcProduct(
        page,
        searchFeild,
        searchBy,
        warehouse_id?,
        stock_condition_id?
    ) {
        let path = `orders/available_products?page[number]=${page}&filter[${searchBy}][0]=${searchFeild}`;
        if (warehouse_id) {
            path = path + `&filter[warehouse_id][0]=${warehouse_id}`;
        }
        if (stock_condition_id) {
            path = path + `&stock_condition_id=${stock_condition_id}`;
        }
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    searcKitProduct(page, searchFeild, searchBy, warehouse_id?) {
        let path = `orders/available_products?called_from=purchase_orders&page[number]=${page}&filter[${searchBy}][0]=${searchFeild}`;
        if (warehouse_id) {
            path = path + `&filter[warehouse_id][0]=${warehouse_id}`;
        }
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getBatches(product_id, expiry_date, page, warehouse_id?) {
        let path = `orders/options?option_type=number&product_id=${product_id}&page[number]=${page}`;
        if (warehouse_id) {
            path = path + `&warehouse_id=${warehouse_id}`;
        }
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getExpiry(product_id, page, warehouse_id?) {
        let path = `orders/options?option_type=expiry_date&product_id=${product_id}&page[number]=${page}`;
        if (warehouse_id) {
            path = path + `&warehouse_id=${warehouse_id}`;
        }
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    createB2COrder(params) {
        const path = 'orders/b2c';
        return this.httpService.callAuthPostApi(path, params, 'jsonApi');
    }

    getProductTemplateFile() {
        const path = 'v1/masters/templates?type=6';
        return this.httpService.getFileAsBlob(path);
    }

    uploadOrderInvoice(id, files) {
        const path = 'orders/' + id + '/invoice';
        return this.httpService.callUploadFilesPostServiceApi(
            path,
            files,
            'invoice',
            'jsonApi'
        );
    }

    getB2COrderData(id) {
        const path = 'orders/' + id + '/b2c_order_dtls';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    updateB2COrder(id, data) {
        const path = 'orders/' + id + '/b2c_order';
        return this.httpService.callPutApi(path, data, 'jsonApi');
    }

    private getOrderMenuCountIfAllowed<T>(
        request: () => Observable<T>
    ): Observable<T> {
        if (this.dataTransferService.shouldDeferOrderMenuCounts()) {
            return EMPTY;
        }
        return request();
    }

    getOrdersCount() {
        return this.getOrderMenuCountIfAllowed(() => {
            const path = `orders/counts`;
            return this.httpService.callAuthGetApi(path, 'jsonApi');
        });
    }

    getProblemOrderCount() {
        return this.getOrderMenuCountIfAllowed(() => {
            const path = `orders/problem_order_counts`;
            return this.httpService.callAuthGetApi(path, 'jsonApi');
        });
    }

    getOrderSource() {
        const path = 'orders/sources';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    getOrderTypes() {
        const path = 'orders/types';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getOrderStatus() {
        const path = 'orders/status';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getOMSOrderCounts() {
        const path = 'orders/oms_order_counts?filter[state]=unsynced';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getServiceTypes() {
        const path = 'orders/service_types';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    resyncOrder(params) {
        const path = 'orders/resync';
        return this.httpService.callAuthPostApi(path, params, 'jsonApi');
    }
    getUnsyncOrdersCount() {
        const path = 'orders/unsync_counts_v1';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    resyncOrderFunc(dataBody) {
        this.resyncOrder(dataBody).subscribe(
            (res) => {
                if (res['result'] === 100) {
                    this.notificationService.setMessage(
                        res['result'],
                        res['message']
                    );
                }
            },
            (error) => {
                this.notificationService.setMessage(101, error.message);
            }
        );
    }

    getSuppliersList() {
        const path = 'products/supplier';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getOrderTemplateFile(orderTypeObj, isAsendiaUser) {
        let path = 'v1/masters/templates?type=5';
        if (isAsendiaUser) {
            path = `v1/masters/templates?type=15&prod_type=${orderTypeObj?.value}`;
        }
        return this.httpService.getFileAsBlob(path);
    }

    getB2bOrderTemplateFile() {
        const path = 'v1/masters/templates?type=11';
        return this.httpService.getFileAsBlob(path);
    }

    getShipmentsTemplateFile(shippingType) {
        const path = `v1/masters/templates?type=8&prod_type=${shippingType}`;
        return this.httpService.getFileAsBlob(path);
    }

    bulkUploadB2cOrderData(file) {
        const path = 'orders/import';
        return this.httpService.callUploadFileServiceApi(path, file, 'jsonApi');
    }

    bulkUploadB2bOrderData(file) {
        const path = 'orders/b2b_import';
        return this.httpService.callUploadFileServiceApi(path, file, 'jsonApi');
    }

    bulkUploadShipmentData(file, params, shippingType, form_type) {
        const path = `orders/shipment_import?prod_type=${form_type}&shipping_type_id=${shippingType}`;
        return this.httpService.callUploadFileServiceApi(
            path,
            file,
            'jsonApi',
            shippingType
        );
    }

    bulkUploadB2CAsendia(file, prodId) {
        const path = 'orders/import_b2c_order_sg';
        return this.httpService.callUploadFileServiceApi(
            path,
            file,
            'jsonApi',
            prodId
        );
    }

    bulkUploadOrderPdtData(file, warehouseId) {
        let path = `frontend/v1/orders/product_import`;
        if (warehouseId) {
            path = path + `?warehouse_id=${warehouseId}`;
        }
        return this.httpService.callUploadFileServiceApi(path, file, 'blob');
    }

    bulkUploadReturnOrderData(file) {
        const path = `orders/return_order_import`;
        return this.httpService.callUploadFileServiceApi(path, file, 'jsonApi');
    }

    getMarketplaceCount() {
        const path = `frontend/v1/marketplaces/counts`;
        return this.httpService.callAuthGetApi(path);
    }
    getCustomerDetails() {
        let path = `frontend/v1/login/login_customer_deatils`;
        return this.httpService.callAuthGetApi(path);
    }
    getDocType() {
        let path = `frontend/v1/orders/doc_types`;
        return this.httpService.callAuthGetApi(path);
    }
    getLabel() {
        let path = 'v1/masters/get_label_formats';
        return this.httpService.callAuthGetMasterApi(path);
    }

    exportOrder(type, shippingType?, orderType?) {
        let path;
        const filterType = type;
        const filtershippingType = `&filter[${
            type == 'marketplaces' ? 'sources' : 'shipping_type'
        }][0]=${shippingType}`;

        // If orderType is rts-failure, build missing_labels_type filter
        const filterMissingLabels =
            orderType === 'rts-failure'
                ? '&filter[missing_labels_type][0]=1&filter[missing_labels_type][1]=2'
                : '';

        path = `orders/export_orders?${
            shippingType && shippingType != 'all-orders'
                ? filterType + filtershippingType + filterMissingLabels
                : filterType + filterMissingLabels
        }`;

        return this.httpService.getFileAsBlob(path, 'jsonApi');
    }

    exportShipment(orderId) {
        const path = `orders/${orderId}/shipment_export`;
        return this.httpService.getFileAsBlob(path, 'jsonApi');
    }

    confirmShipment(orderId, params, action?) {
        const path = `frontend/v1/orders/${orderId}/update_state?${
            action ? `action=${action}` : ''
        }`;
        return this.httpService.callPutApi(path, params);
    }

    deleteShipment(orderId, params) {
        const path = `frontend/v1/orders/${orderId}/update_state?action=delete`;
        return this.httpService.callPutApi(path, params);
    }

    createShipment(params) {
        const path = 'orders/b2c_v1';
        return this.httpService.callAuthPostApi(path, params, 'jsonApi');
    }

    createOrders(params) {
        const path = 'orders/create';
        return this.httpService.callAuthPostApi(path, params, 'jsonApi');
    }

    updateOrder(id, data) {
        const path = 'orders/' + id + '/update';
        return this.httpService.callPutApi(path, data, 'jsonApi');
    }

    getOrderDetails(orderId) {
        const path = `frontend/v1/orders/${orderId}/order_dtls`;
        return this.httpService.callAuthGetApi(path);
    }

    getOmsCountries() {
        const path = `frontend/v1/orders/oms_countries`;
        return this.httpService.callAuthGetApi(path);
    }

    getOmsShippingCarrierDetails(orderId, number) {
        const path = `frontend/v1/orders/${orderId}/oms_shipping_carrier_details?number=${encodeURIComponent(number)}`;
        return this.httpService.callAuthGetApi(path);
    }

    getOmsLineItems(orderId, number) {
        const path = `frontend/v1/orders/${orderId}/oms_line_items?number=${encodeURIComponent(number)}`;
        return this.httpService.callAuthGetApi(path);
    }

    getOmsOrderAndShipment(orderId, shipmentId) {
        const path = `frontend/v1/orders/${orderId}/oms_get_order_and_shipment?shipment_id=${shipmentId}`;
        return this.httpService.callAuthGetApi(path);
    }

    getOmsCustomerDetails(orderId, number) {
        const path = `frontend/v1/orders/${orderId}/oms_customer_details?number=${encodeURIComponent(number)}`;
        return this.httpService.callAuthGetApi(path);
    }

    updateBillAndShipAddress(orderId, data) {
        const path = `frontend/v1/orders/${orderId}/update_bill_and_ship_address`;
        return this.httpService.callPutApi(path, data);
    }

    updateOmsPudoDetails(orderId, pudoPickupLocationId) {
        const path = `frontend/v1/orders/${orderId}/oms_update_pudo_details`;
        return this.httpService.callAuthPostApi(path, {
            pudo_pickup_location_id: pudoPickupLocationId
        });
    }

    getPriceTags() {
        const path = 'v1/masters/get_price_tag_formats';
        return this.httpService.callAuthGetMasterApi(path);
    }

    getShipmentDetails(
        orderId,
        omsOrderId: any = null,
        orderNumber: any = null,
        url: any = 'shipment_order_dtls'
    ) {
        // Strip accidental query/path junk from ids so we never build
        // e.g. orders/29051471?order_number=NCS7698/shipment_order_dtls
        const cleanOrderId = String(orderId ?? '')
            .split('?')[0]
            .split('/')[0]
            .trim();
        const cleanOmsOrderId = omsOrderId
            ? String(omsOrderId).split('?')[0].split('/')[0].trim()
            : null;
        const isDraftDetails =
            typeof url === 'string' &&
            url.includes('draft_order_details') &&
            !!cleanOmsOrderId;

        let path = '';
        if (isDraftDetails) {
            const encodedOrderNumber = encodeURIComponent(orderNumber ?? '');
            path = `frontend/v1/orders/draft_order_details?order_id=${cleanOrderId}&order_number=${encodedOrderNumber}&shipment_id=${cleanOmsOrderId}`;
        } else {
            path = `frontend/v1/orders/${cleanOrderId}/shipment_order_dtls`;
        }
        return this.httpService.callAuthGetApi(path);
    }

    uploadAttachments(id, files) {
        const path = 'orders/' + id + '/invoice';
        return this.httpService.callUploadFilesPostServiceApi(
            path,
            files,
            'invoice',
            'jsonApi'
        );
    }

    deleteAttachments(shipping_document_id) {
        const path =
            'frontend/v1/orders/' + shipping_document_id + '/delete_invoice';
        return this.httpService.callPutApi(path);
    }

    updateShipment(id, data) {
        const path = 'orders/' + id + '/b2c_order_v1';
        return this.httpService.callPutApi(path, data, 'jsonApi');
    }

    generateManifest(orderId) {
        const path = `orders/${orderId}/manifest?timezone_offset=${this.timeZone}`;
        return this.httpService.getFileAsBlob(path, 'jsonApi');
    }

    getShippingLabel(orderId) {
        const path = `frontend/v1/orders/shipping_lebels?order_ids=` + orderId;
        return this.httpService.callAuthGetApi(path).pipe(
            map((response) => {
                if (!response?.data || !Array.isArray(response.data)) {
                    return response;
                }

                const filteredData = response.data.map((order) => {
                    const orderDocuments = order?.documnets || order?.documents;
                    if (!Array.isArray(orderDocuments)) {
                        return order;
                    }

                    const filteredDocuments = orderDocuments.filter(
                        (document) => {
                            const documentUrl = document?.url;
                            return (
                                typeof documentUrl !== 'string' ||
                                !documentUrl.toLowerCase().endsWith('.txt')
                            );
                        }
                    );

                    if (Array.isArray(order?.documnets)) {
                        return {
                            ...order,
                            documnets: filteredDocuments
                        };
                    }

                    return {
                        ...order,
                        documents: filteredDocuments
                    };
                });

                return {
                    ...response,
                    data: filteredData
                };
            })
        );
    }

    updateProductComposition(orderId, params) {
        const path = `orders/${orderId}/update_product_composition`;
        return this.httpService.callAuthPostApi(path, params, 'jsonApi');
    }

    getTrackingEvents(orderId, pageNumber) {
        const path = `frontend/v1/orders/${orderId}/events?page[number]=${pageNumber}&page[size]=10`;
        return this.httpService.callAuthGetApi(path);
    }

    getUomsData() {
        const path = 'products/uoms';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    downloadShipmentLabel(orderIds) {
        const path = `orders/shipping_lebelsPDF?order_ids=${orderIds}`;
        return this.httpService.getFileAsBlob(path, 'jsonApi');
    }

    downloadCommercialNote(orderIds) {
        const path = `orders/download/commercial_note?order_ids=${orderIds}`;
        return this.httpService.getFileAsBlob(path, 'jsonApi');
    }

    /**
     * JDL: pick lists and / or invoices of up to 200 orders as one PDF (OEM-7406). The
     * skipped orders come in the X-Skipped-Orders and X-Skipped-Count headers.
     */
    downloadPickDocuments(orderIds: number[], documents: JdDocuments) {
        return this.httpService.postFileAsBlobResponse(
            'orders/pick_documents',
            { order_ids: orderIds, documents },
            'jsonApi'
        );
    }

    downloadCommercialInvoice(orderIds) {
        const path = `orders/download/commercial_invoice?order_ids=${orderIds}`;
        return this.httpService.getFileAsBlob(path, 'jsonApi');
    }

    downloadZPLShipmentLabel(orderIds) {
        let path = `orders/shipping_lebelsZPL?order_ids=${orderIds}`;
        return this.httpService.getFileAsBlob(path, 'jsonApi');
    }

    typeOfOrders() {
        const path = `orders/return_order_types`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    reasonOfReturn() {
        const path = `orders/return_reasons`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    isReturnable(orderId) {
        const path = `orders/is_order_returnable?order_number_or_tracking_no=${encodeURIComponent(
            orderId
        )}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    createReturnOrder(orderId, params) {
        const path = `orders/${orderId}/intitiate_return`;
        return this.httpService.callAuthPostApi(path, params, 'jsonApi');
    }

    getMarketplacesOrderCount() {
        return this.getOrderMenuCountIfAllowed(() => {
            const path = 'orders/marketplace_counts';
            return this.httpService.callAuthGetApi(path, 'jsonApi');
        });
    }

    getOnlineOrdersCount() {
        return this.getOrderMenuCountIfAllowed(() => {
            const path = 'orders/marketplace_counts_v1';
            return this.httpService.callAuthGetApi(path, 'jsonApi');
        });
    }

    bulkConfirmShipments(orderIds) {
        const path = `orders/bulk_confirmation_shipmemts?order_ids=${orderIds}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    cancelReturnOrder(order_id, param) {
        const path = `orders/${order_id}/cancel_return`;
        return this.httpService.callPutApi(path, param, 'jsonApi');
    }
    downloadManifest(orderIds, orderStatus?) {
        let path = `orders/export_orders?filter[order_ids]=${orderIds}`;
        if (
            orderStatus == 'system-sync' ||
            orderStatus == 'oxm-marketplace-sync'
        ) {
            const missingLabelType = orderStatus == 'system-sync' ? 1 : 2;
            path = `${path}&filter[type]=marketplaces&filter[missing_labels_type][0]=${missingLabelType}`;
        }
        return this.httpService.getFileAsBlob(path, 'jsonApi');
    }

    missingLabelExportOrder(type, missingType) {
        const filterType = `filter[type]=${type}`;
        const missingLabelType = `filter[missing_labels_type][0]=${
            missingType == 'system-sync' ? 1 : 2
        }`;
        const path = `orders/export_orders?${filterType}&${missingLabelType}`;
        return this.httpService.getFileAsBlob(path, 'jsonApi');
    }
    exportDraftOrder(
        pageSize = 10000,
        pageNumber = 1,
        sellusellarStoreId?,
        state = 'draft,reject,rejection_inprocess,approval_inprocess',
        include = 'seller_marketplace,line_item_details'
    ) {
        let queryParams = [];

        if (sellusellarStoreId) {
            queryParams.push(
                `filter[sellusellar_store_id][0]=${sellusellarStoreId}`
            );
        }

        queryParams.push(`filter[state][0]=${state}`);
        queryParams.push(`page[size]=${pageSize}`);
        queryParams.push(`page[number]=${pageNumber}`);
        queryParams.push(`include=${include}`);

        let path = `frontend/v1/orders/export_draft_orders?${queryParams.join(
            '&'
        )}`;
        return this.httpService.getFileAsBlob(path, 'jsonApi');
    }

    deleteShipments(orderIds, data) {
        const path = `orders/bulk_delete_shipmemts?order_ids=${orderIds}`;
        return this.httpService.callAuthDeleteApi(path, data, 'jsonApi');
    }

    getRetailers() {
        const path = `dashboard/retailers`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getReturnOrderTemplateFile() {
        const path = 'v1/masters/templates?type=14';
        return this.httpService.getFileAsBlob(path);
    }

    createAsendiaB2C(params) {
        const path = 'orders/asendia_b2c';
        return this.httpService.callAuthPostApi(path, params, 'jsonApi');
    }

    updateAsendiaB2C(id, data) {
        const path = 'orders/' + id + '/asendia_b2c';
        return this.httpService.callPutApi(path, data, 'jsonApi');
    }

    getCitiesList(id) {
        const path = 'v1/masters/cities?country_id=' + id;
        return this.httpService.callAuthGetMasterApi(path);
    }

    updateDPDActions(orderId, params, action) {
        const path = `orders/${orderId}/update_state?action=${action}`;
        return this.httpService.callPutApi(path, params, 'jsonApi');
    }

    /** JDL Pack Order and Bulk Pack Order: POST orders/bulk_rts, 1 to 200 orders. */
    packJdOrders(orderIds: Array<number | string>) {
        const path = `orders/bulk_rts`;
        return this.httpService.callAuthPostApi(
            path,
            { order_ids: orderIds.map(Number) },
            'jsonApi'
        );
    }

    updateDPDBulkActions(params, actionType) {
        let path;
        if (actionType == 'pack') {
            return this.bulkConfirmShipments(params);
        } else {
            path = `orders/bulk_state_update`;
        }
        return this.httpService.callPutApi(path, params, 'jsonApi');
    }

    bulkCancelB2COrders(orderIds: number[], reason?: string) {
        const path = `frontend/v1/orders/bulk_cancel_b2c`;
        const body: any = { order_ids: orderIds };
        if (reason) {
            body.reason = reason;
        }
        return this.httpService.callPutApi(path, body);
    }

    postTriggerPrintJob(orderIds, sort?) {
        let path;
        if (sort != undefined) {
            path = `orders/print_shipping_lable?sort=${sort}`;
        } else {
            path = `orders/print_shipping_lable`;
        }
        return this.httpService.callAuthPostApi(
            path,
            {
                order_ids: orderIds
            },
            'jsonApi'
        );
    }

    getShippingCarrier() {
        const path = `frontend/v1/orders/carriers`;
        return this.httpService.callAuthGetApi(path);
    }

    changeMarketplaceCarrier(params) {
        const path = 'orders/add_shipping_carrier';
        return this.httpService.callAuthPostApi(path, params, 'jsonApi');
    }

    getSelectedWarehousesByCustomer() {
        const path = `warehouses/get_selected_warehouses_by_customer`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    assignlineitem(id, params) {
        const path = `orders/${id}/item_package_assignment`;
        return this.httpService.callAuthPostApi(path, params, 'jsonApi');
    }

    orderLineItem(id) {
        const path = `orders/order_line_items?order_id=${id}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    callLineItem(id) {
        const path = `orders/package_line_items?order_id=${id}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    callSMPCarrier(seller_marketplace_id, pageno = 1, pagesize = 10) {
        const path = `carrier_rules/smp_shipping_methods?seller_marketplace_id=${seller_marketplace_id}&page[number]=${pageno}&page[size]=${pagesize}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    globalSearch(search) {
        const path = `frontend/v1/orders/order_search?filter[search_pattern][0]=${search}`;
        return this.httpService.callAuthGetApi(path);
    }

    getProcessFlow(
        order_id,
        shipment_id: any = null,
        isDraftOrder: boolean = false
    ) {
        let path = `frontend/v1/orders/${order_id}/order_process_flow`;
        if (isDraftOrder && shipment_id && order_id) {
            path = `orders/${order_id}/get_oms_order_state?shipment_id=${shipment_id}`;
            return this.httpService.callAuthGetApi(path, 'jsonApi');
        } else {
            return this.httpService.callAuthGetApi(path);
        }
    }

    getRejectOrderReasons() {
        const path = `orders/reject_reason`;
        return this.httpService.callAuthGetApi(path);
    }

    returnOrderAction(orderId, params) {
        const path = `orders/${orderId}/approve_reject_return_order`;
        return this.httpService.callPutApi(path, params, 'jsonApi');
    }

    getSyncOrders(orderIds) {
        const encodedOrderIds = encodeURIComponent(orderIds);
        const path = `orders/sync_selected_orders_oms?filter[order_numbers]=${encodedOrderIds}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    exportBulkParcel(size?: any, qs?: any, item?: any) {
        // console.log('item', item);
        let path;
        path =
            `parcels/export_parcels?filter[status]=` +
            qs.status +
            `&page[number]=1` +
            `&page[search_by]=order_number` +
            `&page[size]=` +
            size +
            `&filter[order_number]=` +
            JSON.stringify(item);
        if (qs.period && qs.period != '-') {
            path += `&filter[period]=` + qs.period;
        } else {
            if (qs.startDate) {
                path += `&filter[start_date_time]=` + qs.startDate;
            }
            if (qs.endDate) {
                path += `&filter[end_date_time]=` + qs.endDate;
            }
        }

        // }
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    exportParcel(
        type?: any,
        size?: any,
        range?: string,
        pageNumber?: any,
        dateRange?
    ) {
        let path;
        // if (type == 'draft') {
        //     path =
        //         `frontend/v1/parcels/export_parcels?filter[type]=draft&filter[page_size]=` +
        //         size +
        //         `&filter[period]=` +
        //         range;
        // } else if (type == 'confirmed') {
        //     path =
        //         `frontend/v1/parcels/export_parcels?filter[shipping_type]=all&filter[type]=confirmed&filter[page_size]=` +
        //         size +
        //         `&filter[period]=` +
        //         range;
        // } else if (type == 'return') {
        //     path =
        //         `frontend/v1/parcels/export_parcels?filter[shipping_type]=all&filter[type]=confirmed&filter[page_size]=` +
        //         size +
        //         `&filter[period]=` +
        //         range;
        // } else if (type == 'marketplaces') {
        //     path =
        //         `frontend/v1/parcels/export_parcels?filter[type]=marketplaces&filter[page_size]=` +
        //         size +
        //         `&filter[period]=` +
        //         range;
        // } else {
        // Added Services for export parcels
        path =
            `parcels/export_parcels?filter[status]=` +
            type +
            `&page[number]=` +
            pageNumber +
            `&page[size]=` +
            size;
        if (range && range != '-') {
            path += `&filter[period]=` + range;
        } else {
            if (dateRange.startDate) {
                path += `&filter[start_date_time]=` + dateRange.startDate;
            }
            if (dateRange.endDate) {
                path += `&filter[end_date_time]=` + dateRange.endDate;
            }
        }

        // }
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    exportConfirmedOrder(
        type: string,
        shippingTypeId: string | number,
        status: number,
        range: string
    ) {
        let path = `orders/export_orders?filter[type]=${type}&filter[shipping_type][0]=${shippingTypeId}&filter[created_at]=${range}`;

        switch (status) {
            case 0:
                break;

            case 10:
                path = `${path}&filter[substatus][0]=Delivered`;
                break;

            default:
                path = `${path}&filter[status][0]=${status}`;
                break;
        }

        return this.httpService.getFileAsBlob(path, 'jsonApi');
    }

    getParcelTrackingEvents(params) {
        const path = 'parcels/parcel-tracking/events';
        return this.httpService.callAuthPostApi(path, params, 'jsonApi');
    }

    private parcelTrackingEvent: BehaviorSubject<any> = new BehaviorSubject('');
    public parcelTrackingEvent$ = this.parcelTrackingEvent.asObservable();

    setParcelTrackingRow(data) {
        return this.parcelTrackingEvent.next(data);
    }

    getParcelTrackingRow() {
        let data;
        this.parcelTrackingEvent$.subscribe((value) => {
            data = value;
        });
        return data;
    }

    getDispatchNote(orderId) {
        const path = `orders/dispatch_note?order_id=${orderId}`;
        return this.httpService.getFileAsBlob(path, 'jsonApi');
    }

    getLogoImage(customerId) {
        const cacheKey = String(customerId);
        if (!this.logoImageCache.has(cacheKey)) {
            const path = `users/get_dispatch_note_details?customer_id=${customerId}`;
            const request$ = this.httpService
                .callAuthGetApi(path, 'jsonApi')
                .pipe(shareReplay(1));
            this.logoImageCache.set(cacheKey, request$);
        }
        return this.logoImageCache.get(cacheKey);
    }

    getMasterOrderTemplateFile(template_data) {
        const path = `v1/masters/templates?type=17&template_name=${template_data.template_name}&file_type=${template_data.file_type}`;
        return this.httpService.getFileAsBlob(path);
    }
    getOrdersFile(data, type, filter, range?) {
        const path = `orders/export_ASUK?filter[file_type]=${type}&${filter}&filter[created_at]=${range}`;
        return this.httpService.postFileAsBlob(path, data, 'jsonApi');
    }
    getOrderServiceTypes() {
        const path = `orders/shipping_types?called_for=shipment`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    getparcelServiceTypes() {
        const path = 'parcels/parcel_services';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    bulkUploadMasterOrderTemplateFile(file, template_name) {
        const path = `orders/import_ASUK`;
        return this.httpService.callUploadFileServiceApi(
            path,
            file,
            'jsonApi',
            template_name
        );
    }

    getFormsTemplate(templateId) {
        const path = `orders/${templateId}/template_rules`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    carrierByConf(configurationData) {
        const path = `orders/carrier_by_configuration?shipping_type_id=${configurationData.delivery_type}&source_country_id=${configurationData.sender_country_code}&destination_country_id=${configurationData.country_id}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    // Perfectly working code in bundle
    // compareAndMarkEditableFields(obj1, obj2) {
    //     const editedFields = new Set();

    //     function compareValues(key, value1, value2) {
    //         if (typeof value1 === 'object' && value1 !== null && !Array.isArray(value1) &&
    //             typeof value2 === 'object' && value2 !== null && !Array.isArray(value2)) {
    //             // Compare nested objects
    //             const allKeys = new Set([...Object.keys(value1), ...Object.keys(value2)]);
    //             for (const subKey of allKeys) {
    //                 compareValues(`${key}.${subKey}`, value1[subKey], value2[subKey]);
    //             }
    //         } else if (Array.isArray(value1) && Array.isArray(value2)) {
    //             // Compare arrays, but handle custom_information differently
    //             compareArrays(key, value1, value2);
    //         } else {
    //             // Compare primitive values and ensure null/undefined mismatches are caught
    //             if (value1 !== value2 && !(value1 == null && value2 == null)) {
    //                 editedFields.add(key);
    //             }
    //         }
    //     }

    //     function compareArrays(key, arr1, arr2) {
    //         const minLength = Math.min(arr1.length, arr2.length);

    //         // Compare existing elements in the array
    //         for (let i = 0; i < minLength; i++) {
    //             compareValues(`${key}[${i}]`, arr1[i], arr2[i]);
    //         }

    //         // If new elements are added in custom_information, only add index
    //         if (arr2.length > arr1.length && key === "b2c_order.custom_information") {
    //             for (let i = arr1.length; i < arr2.length; i++) {
    //                 editedFields.add(`${key}[${i}]`);
    //             }
    //         }
    //         // Otherwise, if length mismatch in non-custom_information, mark full key
    //         else if (arr1.length !== arr2.length) {
    //             editedFields.add(key);
    //         }
    //     }

    //     // Compare all top-level keys
    //     const allTopKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);
    //     for (const key of allTopKeys) {
    //         compareValues(key, obj1[key], obj2[key]);
    //     }

    //     return Array.from(editedFields);
    // }

    compareAndMarkEditableFields(obj1, obj2) {
        const editedFields = [];

        // Compare all top-level keys from both objects
        this.compareAllKeys(obj1, obj2, editedFields);

        return editedFields;
    }

    // Step 1: Compare all top-level keys from both objects
    compareAllKeys(obj1, obj2, editedFields) {
        const allTopKeys = new Set([
            ...Object.keys(obj1),
            ...Object.keys(obj2)
        ]);
        allTopKeys.forEach((key) =>
            this.compareValues(key, obj1[key], obj2[key], editedFields)
        );
    }

    // Step 2: Compare values for each key
    compareValues(key, value1, value2, editedFields) {
        if (value1 === value2 || (value1 == null && value2 == null)) return; // Skip if equal or both null/undefined

        const compareObject = this.isObject(value1) && this.isObject(value2);
        const compareArray = Array.isArray(value1) && Array.isArray(value2);
        if (compareObject) {
            this.compareObjects(key, value1, value2, editedFields);
        } else if (compareArray) {
            this.compareArrays(key, value1, value2, editedFields);
        } else {
            editedFields.push(key);
        }
    }

    // Step 3: Compare objects (nested objects)
    compareObjects(key, obj1, obj2, editedFields) {
        const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);
        allKeys.forEach((subKey) =>
            this.compareValues(
                `${key}.${subKey}`,
                obj1[subKey],
                obj2[subKey],
                editedFields
            )
        );
    }

    // Step 4: Compare arrays (handle custom array logic)
    compareArrays(key, arr1, arr2, editedFields) {
        const minLength = Math.min(arr1.length, arr2.length);

        // Compare elements within the common length
        this.compareArrayElements(key, arr1, arr2, minLength, editedFields);

        // Handle array length mismatch
        if (arr2.length !== arr1.length) {
            this.handleArrayLengthMismatch(
                key,
                arr1.length,
                arr2.length,
                editedFields
            );
        }
    }

    // Step 5: Compare array elements
    compareArrayElements(key, arr1, arr2, minLength, editedFields) {
        for (let i = 0; i < minLength; i++) {
            this.compareValues(`${key}[${i}]`, arr1[i], arr2[i], editedFields);
        }
    }

    // Step 6: Handle array length mismatches
    handleArrayLengthMismatch(key, arr1Length, arr2Length, editedFields) {
        if (key === 'b2c_order.custom_information') {
            this.handleCustomInformationArrayLength(
                arr1Length,
                arr2Length,
                editedFields
            );
        } else {
            editedFields.push(key);
        }
    }

    // Step 7: Handle custom "b2c_order.custom_information" array length mismatch
    handleCustomInformationArrayLength(arr1Length, arr2Length, editedFields) {
        for (let i = arr1Length; i < arr2Length; i++) {
            editedFields.push(`b2c_order.custom_information[${i}]`);
        }
    }

    // Step 8: Check if a value is an object
    isObject(value) {
        return (
            typeof value === 'object' && value !== null && !Array.isArray(value)
        );
    }

    getUnsyncOrderDetails(orderno, store) {
        const encodedOrder = encodeURIComponent(orderno);
        const encodedStore = encodeURIComponent(store);
        const path = `orders/unsync_order_details?unsync_order=true&order_number=${encodedOrder}&store=${encodedStore}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    updatePudo(params) {
        const path = `orders/update_pudo_details`;
        return this.httpService.callPutApi(path, params, 'jsonApi');
    }
    getB2COrders(path) {
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    getparcelOrders(path) {
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    getReturnOrdersService(path) {
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    getUnsyncOrdersService(path) {
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    getDraftOrders(path) {
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    getCommercialNote(orderId) {
        const path =
            `frontend/v1/orders/document/commercial_note?order_ids=` + orderId;
        return this.httpService.callAuthGetApi(path);
    }
    getCommercialInvoice(orderId) {
        const path =
            `frontend/v1/orders/document/commercial_invoice?order_ids=` +
            orderId;
        return this.httpService.callAuthGetApi(path);
    }
    getCSKU(searchPattern: any, smp_id: number, customer_id?: any) {
        const path =
            'products/in_stock_channel_sku_with_search?filter[search_pattern]=' +
            searchPattern +
            '&filter[smp_id]=' +
            smp_id +
            '&filter[customer_id]=' +
            customer_id;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    postCSKU(data: any, order_id: number) {
        const path = 'orders/add_sku_to_line_item';
        return this.httpService.callAuthPostApi(path, data, 'jsonApi');
    }
    getAuditLog(path) {
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }
    getSKUDetails(productid, warehouse_id?) {
        let path = `orders/sku_details_by_kits/${productid}`;
        if (warehouse_id) {
            path = `${path}?warehouse_id=${warehouse_id}`;
        }
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    bulkExportMissingLabelsData(params, ext) {
        let path = 'products/export_product_update?file_type=' + ext;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    retryOrder(params, orderStatus) {
        const path =
            orderStatus === 1 ? 'orders/retry' : 'orders/bulk_pack_process';

        // Transform order_ids to shipment_ids when orderStatus !== 1
        if (orderStatus !== 1 && params.order_ids) {
            params.shipment_ids = params.order_ids.join(',');
            delete params.order_ids;
        }

        return this.httpService.callAuthPostApi(path, params, 'jsonApi');
    }

    getChangedKeys(current: any, previous: any, parentKey = ''): string[] {
        const changedKeys: string[] = [];

        const isObject = (val: any) =>
            val && typeof val === 'object' && !Array.isArray(val);
        const isArray = Array.isArray;

        for (const key of Object.keys(current)) {
            const fullKey = parentKey ? `${parentKey}.${key}` : key;

            if (isArray(current[key]) && isArray(previous?.[key])) {
                current[key].forEach((item: any, index: number) => {
                    if (
                        !previous[key] ||
                        JSON.stringify(item) !==
                            JSON.stringify(previous[key][index])
                    ) {
                        if (isObject(item)) {
                            changedKeys.push(
                                ...this.getChangedKeys(
                                    item,
                                    previous[key]?.[index],
                                    `${fullKey}[${index}]`
                                )
                            );
                        } else {
                            changedKeys.push(`${fullKey}[${index}]`);
                        }
                    }
                });
            } else if (isObject(current[key]) && isObject(previous?.[key])) {
                changedKeys.push(
                    ...this.getChangedKeys(current[key], previous[key], fullKey)
                );
            } else if (current[key] !== previous?.[key]) {
                changedKeys.push(fullKey);
            }
        }

        return changedKeys;
    }

    compareOrderItems(
        current: any[],
        previous: any[],
        basePath?: string,
        excludeKeys?: string[]
    ): string[] {
        const changedPaths: string[] = [];

        current.forEach((currItem, index) => {
            const prevItem = previous?.[index];

            if (!prevItem) {
                changedPaths.push(`${basePath}[${index}]`);
                return;
            }

            for (const key of Object.keys(currItem)) {
                if (excludeKeys.includes(key)) continue;

                const currVal = currItem[key];
                const prevVal = prevItem[key];

                if (currVal !== prevVal) {
                    changedPaths.push(`${basePath}[${index}].${key}`);
                }
            }
        });

        return changedPaths;
    }

    // getChangedFieldsDetailed(
    //     current: any,
    //     previous?: any,
    //     parentKey = '',
    //     excludeKeys: string[] = []
    // ): { field_name: string; old_value: any; new_value: any }[] {
    //     const changedKeys: { field_name: string; old_value: any; new_value: any }[] = [];

    //     const isObject = (val: any) => val && typeof val === 'object' && !Array.isArray(val);
    //     const isArray = Array.isArray;

    //     for (const key of Object.keys(current)) {
    //         if (excludeKeys.includes(key)) continue;

    //         const fullKey = parentKey ? `${parentKey}.${key}` : key;
    //         const currVal = current[key] ?? null;
    //         const prevVal = previous?.[key] ?? null;

    //         if (isArray(currVal) && isArray(prevVal)) {
    //             currVal.forEach((item, index) => {
    //                 const currItem = item ?? null;
    //                 const prevItem = prevVal[index] ?? null;
    //                 const fullPath = `${fullKey}[${index}]`;

    //                 if (!previous[key] || JSON.stringify(item) !== JSON.stringify(previous[key][index])) {
    //                     if (isObject(item)) {
    //                         changedKeys.push(
    //                             ...this.getChangedFieldsDetailed(
    //                                 currItem,
    //                                 prevItem,
    //                                 fullPath,
    //                                 excludeKeys
    //                             )
    //                         );
    //                     } else {
    //                         changedKeys.push({
    //                             field_name: fullPath,
    //                             old_value: prevItem,
    //                             new_value: currItem
    //                         });
    //                     }
    //                 }
    //             });
    //         } else if (isObject(currVal) && isObject(prevVal)) {
    //             changedKeys.push(
    //                 ...this.getChangedFieldsDetailed(currVal, prevVal, fullKey, excludeKeys)
    //             );
    //         } else if (currVal !== prevVal) {
    //             changedKeys.push({
    //                 field_name: fullKey,
    //                 old_value: prevVal,
    //                 new_value: currVal
    //             });
    //         }
    //     }

    //     return changedKeys;
    // }

    getChangedFieldsDetailed(
        current: any,
        previous: any = null,
        parentKey = '',
        excludeKeys: string[] = [],
        excludePathSubstrings: string[] = [], // 👈 new
        forceEmit = false,
        warehouseIdToNameMap?: Map<number | string, string> // Optional warehouse ID to name mapping
    ): { field_name: string; old_value: any; new_value: any }[] {
        const changes: {
            field_name: string;
            old_value: any;
            new_value: any;
        }[] = [];

        const isArray = Array.isArray;
        const isObject = (v: any) => v && typeof v === 'object' && !isArray(v);

        const pathExcluded = (path: string) =>
            excludePathSubstrings.some((s) => path.includes(s));

        const pushLeaf = (path: string, oldVal: any, newVal: any) => {
            // Skip if both old and new are null/undefined
            if (
                (oldVal === null || oldVal === undefined) &&
                (newVal === null || newVal === undefined)
            ) {
                return;
            }

            // Skip if path matches excluded patterns
            if (pathExcluded(path)) return;

            // Push if value changed OR forceEmit is active
            if (forceEmit || newVal !== oldVal) {
                changes.push({
                    field_name: path,
                    old_value: oldVal,
                    new_value: newVal
                });
            }
        };

        // Arrays
        if (isArray(current)) {
            // Special handling for warehouse_ids arrays - compare by ID, not by index
            const isWarehouseIdsArray =
                parentKey === 'warehouse_ids' ||
                parentKey.endsWith('.warehouse_ids');

            if (isWarehouseIdsArray && warehouseIdToNameMap) {
                const currentWarehouseIds = current || [];
                const oldWarehouseIds = isArray(previous) ? previous : [];

                // Create sets for quick lookup
                const currentIdsSet = new Set(currentWarehouseIds);
                const oldIdsSet = new Set(oldWarehouseIds);

                // Check if arrays have identical IDs (no changes) - compare by ID, not by index
                if (
                    currentIdsSet.size === oldIdsSet.size &&
                    [...currentIdsSet].every((id) => oldIdsSet.has(id))
                ) {
                    return []; // No changes, return empty array
                }

                const resultMap = new Map<number, any>();

                // First, process warehouses based on OLD array positions
                // Include ALL warehouses from old array (unchanged, removed)
                oldWarehouseIds.forEach(
                    (warehouseId: number | string, oldIndex: number) => {
                        const warehouseName =
                            warehouseIdToNameMap.get(warehouseId) ||
                            warehouseId;
                        const existsInNew = currentIdsSet.has(warehouseId);

                        if (existsInNew) {
                            // Warehouse exists in both old and new - values are the same
                            resultMap.set(oldIndex, {
                                field_name: `${parentKey}[${oldIndex}]`,
                                old_value: warehouseName,
                                new_value: warehouseName
                            });
                        } else {
                            // Warehouse was removed - keep at original position with new_value = null
                            resultMap.set(oldIndex, {
                                field_name: `${parentKey}[${oldIndex}]`,
                                old_value: warehouseName,
                                new_value: null
                            });
                        }
                    }
                );

                // Process new warehouses (exist in new but not in old)
                // Append them at the end, starting from old array length
                let newWarehouseIndex = oldWarehouseIds.length;
                currentWarehouseIds.forEach((warehouseId: number | string) => {
                    if (!oldIdsSet.has(warehouseId)) {
                        const warehouseName =
                            warehouseIdToNameMap.get(warehouseId) ||
                            warehouseId;
                        resultMap.set(newWarehouseIndex, {
                            field_name: `${parentKey}[${newWarehouseIndex}]`,
                            old_value: null,
                            new_value: warehouseName
                        });
                        newWarehouseIndex++;
                    }
                });

                // Convert map to array, sorted by index
                const warehouseFields = Array.from(resultMap.entries())
                    .sort((a, b) => a[0] - b[0])
                    .map((entry) => entry[1]);

                return warehouseFields;
            }

            // Standard array processing for non-warehouse_ids arrays
            const prevArr = isArray(previous) ? previous : [];
            current.forEach((item, i) => {
                const fullPath = `${parentKey}[${i}]`;
                if (pathExcluded(fullPath)) return;

                if (isObject(item)) {
                    changes.push(
                        ...this.getChangedFieldsDetailed(
                            item,
                            prevArr[i] ?? null,
                            fullPath,
                            excludeKeys,
                            excludePathSubstrings,
                            forceEmit || !isObject(prevArr[i]),
                            warehouseIdToNameMap
                        )
                    );
                } else if (isArray(item)) {
                    changes.push(
                        ...this.getChangedFieldsDetailed(
                            item,
                            prevArr[i] ?? [],
                            fullPath,
                            excludeKeys,
                            excludePathSubstrings,
                            forceEmit || !isArray(prevArr[i]),
                            warehouseIdToNameMap
                        )
                    );
                } else {
                    const prevVal = prevArr[i] ?? null;
                    pushLeaf(fullPath, prevVal, item ?? null);
                }
            });
            return changes;
        }

        // Objects
        if (isObject(current)) {
            const prevObj = isObject(previous) ? previous : {};
            for (const key of Object.keys(current)) {
                if (excludeKeys.includes(key)) continue;

                const fullKey = parentKey ? `${parentKey}.${key}` : key;
                if (pathExcluded(fullKey)) continue;

                const currVal = current[key] ?? null;
                const prevVal = Object.prototype.hasOwnProperty.call(
                    prevObj,
                    key
                )
                    ? prevObj[key]
                    : null;

                if (isObject(currVal)) {
                    changes.push(
                        ...this.getChangedFieldsDetailed(
                            currVal,
                            prevVal,
                            fullKey,
                            excludeKeys,
                            excludePathSubstrings,
                            forceEmit || !isObject(prevVal),
                            warehouseIdToNameMap
                        )
                    );
                } else if (isArray(currVal)) {
                    changes.push(
                        ...this.getChangedFieldsDetailed(
                            currVal,
                            isArray(prevVal) ? prevVal : [],
                            fullKey,
                            excludeKeys,
                            excludePathSubstrings,
                            forceEmit || !isArray(prevVal),
                            warehouseIdToNameMap
                        )
                    );
                } else {
                    pushLeaf(fullKey, prevVal ?? null, currVal);
                }
            }
            return changes;
        }

        // Primitive root
        if (parentKey && !pathExcluded(parentKey)) {
            pushLeaf(parentKey, previous ?? null, current ?? null);
        }
        return changes;
    }

    compareOrderItemsDetailed(
        current: any[],
        previous: any[],
        basePath?: string,
        excludeKeys?: string[]
    ): { field_name: string; old_value: any; new_value: any }[] {
        const changedPaths: {
            field_name: string;
            old_value: any;
            new_value: any;
        }[] = [];

        current.forEach((currItem, index) => {
            const currVal = currItem;
            const prevItem = previous?.[index];
            const fullPath = `${basePath}[${index}]`;

            if (!prevItem) {
                changedPaths.push({
                    field_name: fullPath,
                    old_value: prevItem,
                    new_value: currVal
                });
                return;
            }

            for (const key of Object.keys(currItem)) {
                if (excludeKeys.includes(key)) continue;

                const currVal = currItem[key];
                const prevVal = prevItem[key];
                const fullKey = `${basePath}[${index}].${key}`;

                if (currVal !== prevVal) {
                    changedPaths.push({
                        field_name: fullKey,
                        old_value: prevVal,
                        new_value: currVal
                    });
                }
            }
        });

        return changedPaths;
    }

    checkTelephoneValidation(
        telephoneControlPaths: string[],
        form: FormGroup
    ): boolean {
        return telephoneControlPaths.some((path) => {
            const control: AbstractControl | null = form.get(path);

            return (
                !!control &&
                (control.hasError('pattern') || control.hasError('required')) &&
                control.dirty
            );
        });
    }

    checkOrderCloneable(moduleType: ModuleType) {
        const checkOrderType =
            moduleType?.orderType === 'draft' ||
            moduleType?.orderType === 'confirmed' ||
            moduleType?.orderType === 'marketplaces';

        const checkOrderStatus =
            moduleType?.orderStatus === 'all' ||
            moduleType?.orderStatus === 'new' ||
            moduleType?.orderStatus === 'in-process' ||
            moduleType?.orderStatus === 'completed' ||
            moduleType?.orderStatus === 'delivered' ||
            moduleType?.orderStatus === 'cancelled';

        // const checkProblemCategory = (moduleType?.orderStatus === 'problem-category' && moduleType?.problemCategoryType === "offline");
        // if ((checkOrderType && checkOrderStatus) || checkProblemCategory) {
        //     return true;
        // }
        if (checkOrderType && checkOrderStatus) {
            return true;
        }
        return false;
    }

    // ---- Camera Evidence ----

    private buildEvidenceQuery(
        page: number,
        size: number,
        filters: EvidenceFilters = {}
    ): string {
        const parts = [`page[number]=${page}`, `page[size]=${size}`];
        if (filters.mediaType) {
            parts.push(`filter[media_type]=${filters.mediaType}`);
        }
        if (filters.stage) {
            parts.push(`filter[stage]=${encodeURIComponent(filters.stage)}`);
        }
        if (filters.status) {
            parts.push(`filter[status]=${encodeURIComponent(filters.status)}`);
        }
        if (filters.from) {
            parts.push(`filter[created_at][from]=${filters.from}`);
        }
        if (filters.to) {
            parts.push(`filter[created_at][to]=${filters.to}`);
        }
        return parts.join('&');
    }

    getOrderEvidences(
        variant: OrderVariant,
        orderId: string,
        page: number,
        size: number = CAMERA_EVIDENCE_PAGE_SIZE,
        filters: EvidenceFilters = {}
    ): Observable<EvidenceListResponse> {
        const path = `camera_evidence/${variant}_orders/${encodeURIComponent(
            orderId
        )}/evidences?${this.buildEvidenceQuery(page, size, filters)}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getConsignmentEvidences(
        consignmentId: string,
        page: number,
        size: number = CAMERA_EVIDENCE_PAGE_SIZE,
        filters: EvidenceFilters = {}
    ): Observable<EvidenceListResponse> {
        const path = `camera_evidence/consignments/${encodeURIComponent(
            consignmentId
        )}/evidences?${this.buildEvidenceQuery(page, size, filters)}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    /**
     * All stage keys the camera_evidence API can return, so the Stage filter is
     * complete on first paint instead of only listing stages present on page 1.
     */
    getEvidenceStageOptions(): { label: string; value: string }[] {
        return Object.keys(CAMERA_EVIDENCE_STAGE_LABELS).map((value) => ({
            label: CAMERA_EVIDENCE_STAGE_LABELS[value],
            value
        }));
    }

    fetchMediaUrl(
        hashKey: string,
        disposition: 'inline' | 'attachment' = 'inline'
    ): Observable<MediaUrlResponse> {
        return this.httpService.callAuthPostApi(
            'camera_evidence/media/fetch_url',
            { hash_key: hashKey, disposition },
            'jsonApi'
        );
    }

    humanizeStage(stage: string): string {
        if (!stage) {
            return '-';
        }
        if (CAMERA_EVIDENCE_STAGE_LABELS[stage]) {
            return CAMERA_EVIDENCE_STAGE_LABELS[stage];
        }
        return stage
            .split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ');
    }
}
